import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api/apiClient";
import type { AutoCreatedItemRow, ItemCategory } from "../../api/types";
import { ItemThumb } from "../ItemThumb";
import { ReasonModal } from "../ui/ReasonModal";

const DAYS = 30;

/**
 * G-129 (OD-160) — the items the app created by itself from papers in the
 * last 30 days, so a wrong category is fixed here and a wrong item undone
 * here, without a trip to the Action log. The undo is the Action log's own
 * (the item is removed, its draft returns to the queue, its paper line is
 * unlinked); the category change is the ordinary item edit.
 */
export function AutoCreatedItemsBlock({
  categories,
  reloadTick,
  onUndone,
}: {
  categories: ItemCategory[];
  /** Bumped by the page when a paper was read, so the list shows the new items. */
  reloadTick: number;
  /** The draft is pending again — the queue must reload. */
  onUndone: () => void;
}) {
  const [rows, setRows] = useState<AutoCreatedItemRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(() => new Set());
  const [undoing, setUndoing] = useState<AutoCreatedItemRow | null>(null);
  const [undoBusy, setUndoBusy] = useState(false);
  const [undoError, setUndoError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .listAutoCreatedItems({ days: DAYS })
      .then((list) => {
        if (!cancelled) setRows(list);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load the list");
      });
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  const setBusy = (id: string, on: boolean) =>
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const changeCategory = async (row: AutoCreatedItemRow, categoryId: string) => {
    if (!categoryId || categoryId === row.item.category.id) return;
    setBusy(row.auditLogId, true);
    setError(null);
    try {
      const updated = await api.updateItem(row.item.id, { categoryId });
      setRows((prev) =>
        prev
          ? prev.map((r) =>
              r.auditLogId === row.auditLogId
                ? { ...r, item: { ...r.item, category: updated.category ?? r.item.category } }
                : r,
            )
          : prev,
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not change the category");
    } finally {
      setBusy(row.auditLogId, false);
    }
  };

  if (rows === null && !error) return null;
  if (rows !== null && rows.length === 0 && !error) return null;

  return (
    <div className="card auto-created">
      <div>
        <h3 className="inv-section-title">Created automatically (last {DAYS} days)</h3>
        <p className="muted inv-section-note">
          Items the app made by itself when a paper was read — the reader knew the category. Fix a
          category here, or undo one that should not exist (its line goes back to the queue).
        </p>
      </div>
      {error ? <div className="alert error">{error}</div> : null}
      {rows && rows.length > 0 ? (
        <div className="table-scroll">
          <table className="paper-lines">
            <thead>
              <tr>
                <th aria-label="Photo" />
                <th>Item</th>
                <th>From</th>
                <th>Category</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const busy = busyIds.has(row.auditLogId);
                return (
                  <tr key={row.auditLogId} className={busy ? "is-busy" : undefined}>
                    <td className="auto-created__thumb">
                      <ItemThumb itemId={row.item.id} imageKey={row.item.imageKey} alt={row.item.name} size={40} />
                    </td>
                    <td>
                      <Link to={`/app/items/${row.item.id}`}>{row.item.name}</Link>
                      {row.nameRaw && row.nameRaw !== row.item.name ? (
                        <div className="field-hint">printed as “{row.nameRaw}”</div>
                      ) : null}
                    </td>
                    <td>
                      <div>{row.supplier?.name ?? "—"}</div>
                      <div className="field-hint">
                        {row.paper ? (
                          <Link to={`/app/invoices/${row.paper.id}`}>
                            {row.paper.invoiceNumber ?? row.paper.originalFilename}
                          </Link>
                        ) : (
                          "paper deleted"
                        )}
                        {" · "}
                        {row.createdAt.slice(0, 10)}
                      </div>
                    </td>
                    <td>
                      <select
                        aria-label={`Category for ${row.item.name}`}
                        value={row.item.category.id}
                        disabled={busy}
                        onChange={(e) => void changeCategory(row, e.target.value)}
                      >
                        {categories.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="draft-review__actions">
                      <button
                        type="button"
                        className="button danger"
                        disabled={busy}
                        onClick={() => {
                          setUndoError(null);
                          setUndoing(row);
                        }}
                      >
                        Undo
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {undoing ? (
        <ReasonModal
          title={`Undo the creation of ${undoing.item.name}?`}
          message="The item is removed and its line goes back to the queue, to be created again or linked to an existing item. Refused once stock, a sale or a delivery hangs off the item."
          reasonMode="required"
          reasonPlaceholder="e.g. the reader made it from a footer line"
          confirmLabel="Undo it"
          destructive
          busy={undoBusy}
          error={undoError}
          onConfirm={async (reason) => {
            if (!reason) return;
            setUndoBusy(true);
            setUndoError(null);
            try {
              await api.undoCatalogAction(undoing.auditLogId, reason);
              setRows((prev) => (prev ? prev.filter((r) => r.auditLogId !== undoing.auditLogId) : prev));
              setUndoing(null);
              onUndone();
            } catch (err: unknown) {
              setUndoError(err instanceof Error ? err.message : "Could not undo this change.");
            } finally {
              setUndoBusy(false);
            }
          }}
          onClose={() => setUndoing(null)}
        />
      ) : null}
    </div>
  );
}
